import React, { useState } from 'react';
import PropTypes from 'prop-types';

const ShoppingListItemList = ({ items, onEdit, onDelete, onMarkAsPurchased, onAddToPantry }) => {
  const [isMobile, setIsMobile] = useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const calculateItemTotal = (item) => {
    const quantity = item.quantity || 1;
    const price = item.price || 0;
    return quantity * price;
  };

  if (items.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8 text-center">
        <p className="text-gray-500">Your shopping list is empty. Add items to get started!</p>
      </div>
    );
  }

  // Mobile card view
  if (isMobile) {
    return (
      <div className="space-y-2">
        {items.map((item) => {
          const itemTotal = calculateItemTotal(item);
          return (
            <div
              key={item.id}
              className={`bg-white rounded-lg shadow-sm p-2.5 border-l-4 ${
                item.isPurchased
                  ? 'border-gray-300 opacity-60'
                  : 'border-purple-500'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center space-x-2 flex-1 min-w-0">
                  <input
                    type="checkbox"
                    checked={item.isPurchased || false}
                    onChange={() => onMarkAsPurchased(item.id)}
                    className="w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-sm font-semibold truncate ${
                      item.isPurchased ? 'line-through text-gray-500' : 'text-gray-900'
                    }`}>
                      {item.name}
                    </h3>
                    {item.category && (
                      <p className="text-xs text-gray-500 truncate">{item.category}</p>
                    )}
                  </div>
                </div>
                {!item.isPurchased && (
                  <div className="flex items-center space-x-1 flex-shrink-0">
                    <button
                      onClick={() => onAddToPantry(item.id)}
                      className="p-1.5 text-green-600 hover:bg-green-100 rounded active:bg-green-200"
                      aria-label="Add to pantry"
                      title="Add to pantry"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </button>
                    <button
                      onClick={() => onEdit(item)}
                      className="p-1.5 text-purple-600 hover:bg-purple-100 rounded active:bg-purple-200"
                      aria-label="Edit item"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => onDelete(item.id)}
                      className="p-1.5 text-red-600 hover:bg-red-100 rounded active:bg-red-200"
                      aria-label="Delete item"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between text-xs mt-2 pt-2 border-t border-gray-100">
                <span className="text-gray-600">
                  Qty: <span className="font-medium text-gray-900">{item.quantity} {item.unit || ''}</span>
                </span>
                <span className="text-gray-600">
                  Price: <span className="font-medium text-gray-900">{formatCurrency(item.price)}</span>
                </span>
                <span className="font-semibold text-purple-600">
                  Total: {formatCurrency(itemTotal)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Desktop table view
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Purchased
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Quantity
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Unit Price
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {items.map((item) => {
              const itemTotal = calculateItemTotal(item);
              return (
                <tr
                  key={item.id}
                  className={`hover:bg-gray-50 ${item.isPurchased ? 'opacity-60' : ''}`}
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={item.isPurchased || false}
                      onChange={() => onMarkAsPurchased(item.id)}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500 w-5 h-5"
                    />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className={`text-sm font-medium ${
                      item.isPurchased ? 'line-through text-gray-500' : 'text-gray-900'
                    }`}>
                      {item.name}
                    </div>
                    {item.category && (
                      <div className="text-sm text-gray-500">{item.category}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {item.quantity} {item.unit || ''}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(item.price)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">
                    {formatCurrency(itemTotal)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium space-x-2">
                    {!item.isPurchased && (
                      <button
                        onClick={() => onAddToPantry(item.id)}
                        className="text-green-600 hover:text-green-900 font-medium"
                        title="Add to pantry"
                      >
                        📦 Add to Pantry
                      </button>
                    )}
                    <button
                      onClick={() => onEdit(item)}
                      className="text-purple-600 hover:text-purple-900"
                      disabled={item.isPurchased}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => onDelete(item.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

ShoppingListItemList.propTypes = {
  items: PropTypes.array.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onMarkAsPurchased: PropTypes.func.isRequired,
  onAddToPantry: PropTypes.func.isRequired,
};

export default ShoppingListItemList;
